import React, { useEffect, useMemo, useState } from "react";
import { useStores } from "_common/hooks";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NotificationManager } from "react-notifications";
import { PaymentApi } from "states/api/payment";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatBalance, formatNumber } from "_common/utils/formatValue";
import i18next from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLongArrowAltLeft,
  faChevronRight,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import { useNotify } from "_common/component/NotifyProvider";
import HeaderTop from "_common/component/Layout/Header/HeaderTop";
import classNames from "classnames";
import SelectBox from "_common/component/SelectBox";
import { UserWalletApi } from "states/api/wallet";

const schema = yup
  .object({
    address: yup.string(),
    amount: yup.number().required(i18next.t("verify_require")),
  })
  .required();

export default function WithdrawSubmit() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pushNotify } = useNotify();

  const symbol = params.get("symbol") || "usdt";
  const network = params.get("network");

  const {
    authStore: { user, general, coins, bank_default },
  } = useStores();

  const {
    register,
    trigger,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
    getValues,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const amount = watch("amount");

  const { data, refetch } = useQuery(
    ["user_wallet", "list"],
    () =>
      UserWalletApi.getList({
        params: {
          page: 1,
          limit: 50,
        },
      }),
    {
      staleTime: 300000,
    }
  );

  useEffect(() => {
    refetch();
  }, []);

  const mainCoin = useMemo(
    () => coins?.find((obj) => obj?.symbol === symbol),
    [coins, symbol]
  );

  const mainBalance = useMemo(
    () => user?.tokens?.find((obj) => obj?.symbol === symbol),
    [user, symbol]
  );

  const listWallet = useMemo(() => {
    if (!data?.data?.length) return [];
    return data?.data?.map((item) => ({
      value: item?.id,
      label: `${item?.bank_number_display} (${item?.bank_name})`,
    }));
  }, [data?.data]);

  const { mutate: onSubmit, isLoading } = useMutation(
    (variables) =>
      PaymentApi.withdraw({
        params: {
          ...variables,
          symbol,
          bank_id: listWallet?.[0]?.value,
          type: "withdraw",
        },
      }),
    {
      onSuccess: (res) => {
        pushNotify({
          type: "withdraw",
        });
        reset();
        queryClient.invalidateQueries(["get_profile"]);
        queryClient.invalidateQueries(["payment", "list"]);
        NotificationManager.success(
          i18next.t(res?.msg) || i18next.t("withdrawal_successful")
        );
      },
      onError: (error) => {
        const errorMessage =
          i18next.t(error?.message) ?? i18next.t("action_failed_msg");
        NotificationManager.error(errorMessage);
      },
    }
  );

  const onSave = (values) => {
    if (isLoading) return null;
    if (!listWallet?.[0]?.value)
      return NotificationManager.error("Vui lòng liên kết tài khoản rút tiền");

    if (amount < Number(general?.min_withdraw || 0))
      return NotificationManager.error(
        `${i18next.t("enter_large_amount_msg")} ${formatNumber(
          general?.min_withdraw
        )}`
      );
    if (amount > Number(general?.max_withdraw || 0))
      return NotificationManager.error(
        `${i18next.t("enter_smaller_amount_msg")} ${formatNumber(
          general?.max_withdraw
        )}`
      );
    onSubmit({ ...values });
    return null;
  };

  return (
    <div className="bg-black min-h-screen mt-[70px]">
      <HeaderTop title={i18next.t("withdraw")} />
      <form
        className="flex flex-col items-center gap-4 p-4 bg-black"
        onSubmit={handleSubmit(onSave)}
      >
        <div className="flex flex-col items-center gap-4 w-full">
          <img
            src="https://api.demo92.apptestlive.com/assets/images/atm.svg"
            alt="ATM"
            width="100"
            height="100"
            className="h-40 w-40 mt-4"
          />
          <SelectBox label={i18next.t("account")} options={listWallet} />
          <div className="tx_inputbox">
            <div className="tx_inputbox_l">
              <input
                type="number"
                inputMode="decimal"
                min={0}
                max={1000000000000}
                autoComplete="off"
                className="w-full h-[30px]"
                placeholder={i18next.t("quantity")}
                {...register("amount")}
              />
            </div>
          </div>
          <div className="w-full flex justify-between text-md">
            <div className="text-white">
              {i18next.t("current_balance")}: &nbsp;{" "}
              {formatNumber(user?.balance, "0,0.[000000]")} USDT
            </div>
            <div className="text-[#3d5afe] cursor-pointer">
              {i18next.t("all_balance")}
            </div>
          </div>
          <div className="tx_inputbox">
            <div className="tx_inputbox_l">
              <input
                type="password"
                placeholder={i18next.t("wallet_password")}
                className="w-full h-[30px]"
                {...register("wallet_password")}
              />
            </div>
          </div>
          <Link
            className="w-full py-[6px] px-4 bg-[#3d5afe] hover:bg-[#2a3eb1] text-md font-medium text-white text-center rounded"
            to="/member/wallet-password"
          >
            {i18next.t("set_wallet_pass")}
          </Link>
          <Link
            className="w-full py-[6px] px-4 border border-[#3d5afe80] hover:border-[#3d5afe] text-md font-medium text-[#3d5afe] text-center rounded"
            to="/member/wallet"
          >
            {i18next.t("set_address_wallet")}
          </Link>
          <div
            id="confirm-withdraw"
            className="fixed bottom-0 left-0 flex flex-col gap-2 w-full pb-10 p-4 bg-black"
          >
            <div className="w-full flex justify-between text-lg text-white">
              <span className="text-[#888]">{i18next.t("fee")}</span>
              <span>{formatNumber(amount * 0.02, "0,0.[00000]")} USDT</span>
            </div>
            <div className="w-full flex justify-between text-lg text-white">
              <span className="text-[#888]">{i18next.t("final_total")}</span>
              <span>{formatNumber(amount * 0.98, "0,0.[00000]")} USDT</span>
            </div>
            <button
              type="submit"
              className="w-full py-[6px] px-4 rounded bg-[#3d5afe] hover:bg-[#2a3eb1] text-white"
              style={{ backgroundColor: "rgb(61, 90, 254);" }}
            >
              {i18next.t("confirm")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
